// Public NDIS invoice approval page (mobile-first). The support provider opens
// the link from their email, reviews a 1:1 editable copy of the invoice, then
// Approves (a PDF is generated and the invoice is emailed to the participant's
// plan manager) or Submits edits (returns to the admin for review).
// Everything except the participant's details is editable. No account, no auth —
// the URL token is the credential. Theme matches the support-onboarding test UI.

import { jsPDF } from 'jspdf';
import { API_BASE } from '../api';
import { supportProfileDraftStyles } from './support-profile-setup';

interface LineItem { code?: string; name?: string; unit?: string; date?: string; qty?: number; rate?: number; gst?: number; amount?: number; }
interface Invoice {
  id: string; invoice_number?: string; status: string; line_items: LineItem[];
  subtotal: number; gst_total: number; total: number;
  service_date_from?: string | null; service_date_to?: string | null; notes?: string | null; created_at?: string | null;
}
interface Provider { display_name?: string; email?: string; phone?: string; abn?: string; address?: string; notes?: string; bank_account_name?: string; bank_bsb?: string; bank_account_number?: string; }

export function isNdisApprovePath(): boolean {
  return /\/ndis\/approve\/[A-Za-z0-9_-]+/.test(window.location.pathname);
}
function tokenFromPath(): string {
  const m = window.location.pathname.match(/\/ndis\/approve\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : '';
}
function esc(s: string): string { const d = document.createElement('div'); d.textContent = s ?? ''; return d.innerHTML; }
function money(n: number): string { return `$${(n || 0).toFixed(2)}`; }
// ISO (YYYY-MM-DD) -> DD/MM/YYYY for display; passes through anything else.
function dmy(s: string | null | undefined): string {
  if (!s) return '';
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s);
}

// Invoice-specific styles layered on top of the shared support-onboarding theme.
const INV_STYLES = `
.iv-meta{display:grid;grid-template-columns:1fr;gap:12px}
.iv-party{display:grid;grid-template-columns:1fr;gap:14px}
.iv-ro{background:#f1f8ee;border:1px solid #c8dfc1;border-radius:10px;padding:12px 14px}
.iv-ro span{display:block;font-size:12px;color:#52665d;font-weight:600;margin-bottom:2px}
.iv-ro strong{font-size:16px;color:#18352a}
.iv-line{border:1.5px solid #c9dcc1;border-radius:12px;padding:14px;margin-bottom:12px;background:#fafdf8}
.iv-line-2{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:8px}
.iv-amt{text-align:right;font-weight:800;color:#15803d;margin-top:8px;font-size:16px}
.iv-add{background:#f1f8ee;border:1.5px dashed #9ac99f;color:#15803d;border-radius:10px;padding:12px;width:100%;font-weight:700;font-size:15px;cursor:pointer}
.iv-rm{background:none;border:0;color:#b4503c;font-weight:700;font-size:13px;cursor:pointer;padding:4px 0}
.iv-tot{margin-top:6px}
.iv-tot-row{display:flex;justify-content:space-between;padding:8px 0;border-top:1px solid #e3efdd;color:#3d5a4c}
.iv-tot-big{font-size:24px;font-weight:850;color:#18352a}
.iv-actions{position:sticky;bottom:0;background:#fffaf0;padding:14px 0 calc(14px + env(safe-area-inset-bottom));display:flex;flex-wrap:wrap;gap:10px;margin-top:8px}
.iv-btn{flex:1 1 110px;border:0;border-radius:12px;padding:16px;font-size:16px;font-weight:750;cursor:pointer}
.iv-btn-approve{background:#16a34a;color:#fff}
.iv-btn-edit{background:#fff;color:#15803d;border:1.5px solid #16a34a}
.iv-btn:disabled{opacity:.5}
.iv-btn-reject{background:#fff;color:#b4503c;border:1.5px solid #d98b7d}
.iv-modal-ov{position:fixed;inset:0;background:rgba(20,15,8,.55);display:flex;align-items:flex-end;justify-content:center;z-index:50}
.iv-modal{background:#fffaf0;width:100%;max-width:520px;border-radius:18px 18px 0 0;padding:20px 18px calc(20px + env(safe-area-inset-bottom));box-shadow:0 -8px 40px rgba(0,0,0,.3)}
.iv-modal-h{font-size:19px;font-weight:800;color:#18352a;margin-bottom:6px}
.iv-modal-p{font-size:14px;color:#5a6b60;margin:0 0 12px}
.iv-rj-opt{display:flex;align-items:center;gap:10px;padding:11px 12px;border:1px solid #e3efdd;border-radius:10px;margin-bottom:7px;font-size:15px;color:#27402f;cursor:pointer}
.iv-rj-other{width:100%;margin-top:4px;border:1px solid #cfe0c8;border-radius:10px;padding:10px;font-size:15px;font-family:inherit;box-sizing:border-box}
.iv-modal-btns{display:flex;gap:10px;margin-top:12px}
@media(min-width:520px){.iv-modal-ov{align-items:center}.iv-modal{border-radius:18px}}
.iv-banner{border-radius:12px;padding:14px 16px;margin-bottom:18px;font-size:15px}
.iv-ok{background:#e7f6ec;border:1px solid #8fcf9f;color:#1c5a32}
.iv-doc-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
.iv-line-2{grid-template-columns:1fr}
@media (min-width:480px){.iv-line-2{grid-template-columns:1fr 1fr}}
@media (min-width:520px){.iv-meta{grid-template-columns:1fr 1fr;gap:14px}}
`;

let editing = false;

export async function renderNdisApprove(): Promise<void> {
  document.title = 'Invoice approval · the hub ecosystems';
  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';
  if (!document.getElementById('sp-draft-styles')) {
    const s = document.createElement('style'); s.id = 'sp-draft-styles'; s.textContent = supportProfileDraftStyles(); document.head.appendChild(s);
  }
  const s2 = document.createElement('style'); s2.textContent = INV_STYLES; document.head.appendChild(s2);

  const page = document.createElement('div'); page.className = 'sp-page';
  page.innerHTML = `<main class="sp-shell"><div class="sp-brand">the hub ecosystems</div><div id="iv-content"><div class="sp-section">Loading invoice…</div></div></main>`;
  root.appendChild(page);

  const token = tokenFromPath();
  const content = page.querySelector('#iv-content') as HTMLElement;
  let invoice: Invoice | null = null;
  let provider: Provider | null = null;
  let participantName = 'Participant';
  let participantNdis = '';
  let participantAddress = '';

  try {
    const res = await fetch(`${API_BASE}/api/ndis/approval/${encodeURIComponent(token)}`);
    const json = await res.json();
    if (json.status !== 'ok') throw new Error(json.error);
    invoice = json.data.invoice;
    provider = json.data.provider || {};
    participantName = json.data.participant?.display_name || 'Participant';
    participantNdis = json.data.participant?.ndis_number || '';
    participantAddress = json.data.participant?.address || '';
  } catch {
    content.innerHTML = `<div class="sp-section"><div class="iv-banner" style="background:#fdecea;border:1px solid #f1a9a0;color:#8a2c1e">This approval link is invalid or has expired.</div></div>`;
    return;
  }
  const inv = invoice as Invoice;
  const prov = provider as Provider;

  const draw = (): void => {
    const done = inv.status === 'sent' || inv.status === 'approved';
    const edited = inv.status === 'edited_pending_review';
    const banner = done
      ? `<div class="iv-banner iv-ok"><strong>Approved.</strong> This invoice has been emailed to the plan manager with a PDF attached. Thank you.</div>`
      : edited ? `<div class="iv-banner" style="background:#fdf6e3;border:1px solid #e9d9a8;color:#7a5e12"><strong>Changes submitted.</strong> Your edits were sent back for review.</div>` : '';

    const lines = inv.line_items.map((li, i) => {
      const amt = li.amount ?? (li.qty || 0) * (li.rate || 0);
      if (editing) {
        return `<div class="iv-line" data-i="${i}">
          <label class="sp-field"><span>Date of support</span><input data-f="date" type="date" value="${esc(li.date || '')}"></label>
          <label class="sp-field" style="margin-top:8px"><span>Support / line code</span><input data-f="name" value="${esc(li.name || '')}" placeholder="Support name"></label>
          <input class="sp-field-input" data-f="code" value="${esc(li.code || '')}" placeholder="Line code" style="width:100%;min-height:44px;border:1.5px solid #c9dcc1;border-radius:10px;background:#fff;padding:10px 12px;font-size:15px;box-sizing:border-box;margin-top:8px">
          <div class="iv-line-2">
            <label class="sp-field"><span>Quantity (${esc(li.unit || 'units')})</span><input data-f="qty" type="number" inputmode="decimal" value="${li.qty ?? 0}"></label>
            <label class="sp-field"><span>Rate ($)</span><input data-f="rate" type="number" inputmode="decimal" value="${li.rate ?? 0}"></label>
          </div>
          <div class="iv-amt" data-amt>${money(amt)}</div>
          <button class="iv-rm" data-rm="${i}">Remove line</button>
        </div>`;
      }
      return `<div class="iv-line"><strong>${esc(li.name || li.code || 'Support')}</strong>
        <div style="color:#52665d;font-size:13px;margin-top:3px">${li.date ? esc(dmy(li.date)) + ' · ' : ''}${esc(li.code || '')} · ${li.qty ?? 0} ${esc(li.unit || '')} × ${money(li.rate || 0)}</div>
        <div class="iv-amt">${money(amt)}</div></div>`;
    }).join('');

    content.innerHTML = `
      ${banner}
      <section class="sp-section">
        <div class="iv-doc-head"><div><div class="sp-section-kicker">Tax invoice</div>
          <h2 style="margin:0">${editing ? '' : esc(inv.invoice_number || '')}</h2></div></div>
        ${editing ? `<label class="sp-field" style="margin-top:8px"><span>Invoice number</span><input id="iv-num" value="${esc(inv.invoice_number || '')}"></label>` : ''}
        <div class="iv-meta" style="margin-top:14px">
          <label class="sp-field"><span>Service from</span><input id="iv-from" type="date" value="${esc(inv.service_date_from || '')}" ${editing ? '' : 'disabled'}></label>
          <label class="sp-field"><span>Service to</span><input id="iv-to" type="date" value="${esc(inv.service_date_to || '')}" ${editing ? '' : 'disabled'}></label>
        </div>
      </section>

      <section class="sp-section">
        <div class="sp-section-kicker">From (your details)</div>
        <div class="iv-party">
          <label class="sp-field"><span>Provider name</span><input id="pv-name" value="${esc(prov.display_name || '')}" ${editing ? '' : 'disabled'}></label>
          <label class="sp-field"><span>ABN</span><input id="pv-abn" value="${esc(prov.abn || '')}" ${editing ? '' : 'disabled'}></label>
          <div class="iv-meta">
            <label class="sp-field"><span>BSB</span><input id="pv-bsb" value="${esc(prov.bank_bsb || '')}" ${editing ? '' : 'disabled'}></label>
            <label class="sp-field"><span>Account number</span><input id="pv-acc" value="${esc(prov.bank_account_number || '')}" ${editing ? '' : 'disabled'}></label>
          </div>
        </div>
      </section>

      <section class="sp-section">
        <div class="sp-section-kicker">For (participant)</div>
        <div class="iv-ro"><span>Supported person</span><strong>${esc(participantName)}</strong></div>
        ${participantNdis ? `<div class="iv-ro"><span>NDIS number</span><strong>${esc(participantNdis)}</strong></div>` : ''}
        ${participantAddress ? `<div class="iv-ro"><span>Address</span><strong>${esc(participantAddress)}</strong></div>` : ''}
        <p class="sp-section-copy" style="margin:10px 0 0;font-size:13px">Participant details can't be changed here.</p>
      </section>

      <section class="sp-section">
        <div class="sp-section-kicker">Supports</div>
        <div id="iv-lines">${lines}</div>
        ${editing ? `<button class="iv-add" id="iv-add">+ Add a support line</button>` : ''}
        <div class="iv-tot">
          <div class="iv-tot-row"><span class="iv-tot-big">Invoice total</span><span class="iv-tot-big" id="iv-total">${money(inv.total)}</span></div>
        </div>
      </section>
      ${done || edited ? '' : `
      <section class="sp-section">
        <div class="sp-checks"><label><input type="checkbox" id="iv-confirm"> I confirm these details are correct.</label></div>
      </section>`}
    `;

    const old = page.querySelector('.iv-actions'); if (old) old.remove();
    const bar = document.createElement('div'); bar.className = 'iv-actions';
    const previewBtn = '<button class="iv-btn iv-btn-edit" id="iv-preview">Preview PDF</button>';
    if (editing) {
      bar.innerHTML = `<button class="iv-btn iv-btn-edit" id="iv-cancel">Cancel</button><button class="iv-btn iv-btn-approve" id="iv-submit">Submit edits</button>`;
    } else if (done || edited) {
      // Finalised / already-edited: still let them open the PDF.
      bar.innerHTML = previewBtn;
    } else {
      bar.innerHTML = `${previewBtn}<button class="iv-btn iv-btn-edit" id="iv-edit">Edit invoice</button><button class="iv-btn iv-btn-reject" id="iv-reject">Reject</button><button class="iv-btn iv-btn-approve" id="iv-approve">Approve &amp; send</button>`;
    }
    (page.querySelector('.sp-shell') as HTMLElement).appendChild(bar);

    bar.querySelector('#iv-preview')?.addEventListener('click', previewPdf);
    if (editing) {
      content.querySelectorAll('.iv-line input[data-f]').forEach((el) => el.addEventListener('input', recompute));
      content.querySelector('#iv-add')?.addEventListener('click', () => { inv.line_items.push({ name: '', code: '', unit: 'H', qty: 1, rate: 0, gst: 0, amount: 0 }); syncFromInputs(); draw(); });
      content.querySelectorAll('[data-rm]').forEach((el) => el.addEventListener('click', () => { syncFromInputs(); inv.line_items.splice(parseInt((el as HTMLElement).dataset.rm!, 10), 1); draw(); }));
      bar.querySelector('#iv-cancel')?.addEventListener('click', () => { editing = false; draw(); });
      bar.querySelector('#iv-submit')?.addEventListener('click', submitEdits);
    } else if (!done && !edited) {
      bar.querySelector('#iv-edit')?.addEventListener('click', () => { editing = true; draw(); });
      bar.querySelector('#iv-reject')?.addEventListener('click', openRejectModal);
      bar.querySelector('#iv-approve')?.addEventListener('click', approve);
    }
  };

  const syncFromInputs = (): void => {
    content.querySelectorAll('.iv-line[data-i]').forEach((row) => {
      const i = parseInt((row as HTMLElement).dataset.i!, 10);
      const get = (f: string): string => (row.querySelector(`[data-f="${f}"]`) as HTMLInputElement)?.value || '';
      const qty = parseFloat(get('qty')) || 0; const rate = parseFloat(get('rate')) || 0;
      inv.line_items[i] = { ...inv.line_items[i], name: get('name'), code: get('code'), date: get('date'), qty, rate, amount: Math.round(qty * rate * 100) / 100 };
    });
  };

  const recompute = (): void => {
    syncFromInputs();
    content.querySelectorAll('.iv-line[data-i]').forEach((row) => {
      const i = parseInt((row as HTMLElement).dataset.i!, 10);
      const a = row.querySelector('[data-amt]'); if (a) a.textContent = money(inv.line_items[i].amount || 0);
    });
    const sub = inv.line_items.reduce((s, l) => s + (l.amount || 0), 0);
    inv.subtotal = sub; inv.gst_total = 0; inv.total = sub;
    const set = (id: string, v: string): void => { const e = document.getElementById(id); if (e) e.textContent = v; };
    set('iv-sub', money(sub)); set('iv-total', money(sub));
  };

  const buildPdfDoc = (): jsPDF => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const R = 547; // right content margin
    // ── Title (top-right) + service period beneath it ──
    doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(20);
    doc.text('TAX INVOICE', R, 60, { align: 'right' });
    if (inv.service_date_from) {
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(90);
      doc.text(`Service: ${dmy(inv.service_date_from)}${inv.service_date_to ? ' to ' + dmy(inv.service_date_to) : ''}`, R, 78, { align: 'right' });
      doc.setTextColor(20);
    }
    // ── Company (left) ──
    let provAddr = prov.address || '';
    if (!provAddr && prov.notes) { try { const n = JSON.parse(prov.notes) as { address?: string }; provAddr = n.address || ''; } catch { /* ignore */ } }
    let y = 104;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
    doc.text(`Provider: ${prov.display_name || ''}`, 48, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    y += 18; doc.text(`ABN: ${prov.abn || ''}`, 48, y);
    if (provAddr) { y += 15; doc.text(`Address: ${provAddr}`, 48, y); }
    y += 15; doc.text(`Email: ${prov.email || ''}`, 48, y);
    y += 15; doc.text(`Phone: ${prov.phone || ''}`, 48, y);
    const companyBottom = y;
    // ── Invoice meta (right) ──
    let ry = 104;
    doc.setFontSize(10);
    doc.text(`Invoice no: ${inv.invoice_number || ''}`, 320, ry); ry += 16;
    doc.text(`Invoice date: ${dmy(inv.created_at)}`, 320, ry); ry += 16;
    // ── Divider ──
    y = Math.max(companyBottom, ry) + 16;
    doc.setDrawColor(40); doc.setLineWidth(1.2); doc.line(48, y, R, y); doc.setLineWidth(1); y += 22;
    // ── TO (participant) ──
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text('TO:', 48, y);
    doc.setFont('helvetica', 'normal'); doc.text(participantName, 74, y); y += 15;
    if (participantNdis) { doc.text(`NDIS number: ${participantNdis}`, 74, y); y += 15; }
    if (participantAddress) { doc.text(`Address: ${participantAddress}`, 74, y); y += 15; }
    y += 10;
    // ── Line-item table ──
    const cD = 48, cDesc = 130, cItem = 300, cQty = 405, cRate = 450;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(20);
    doc.setDrawColor(150); doc.setLineWidth(0.5); doc.line(48, y - 11, R, y - 11);
    doc.text('SERVICE DATE', cD, y); doc.text('DESCRIPTION', cDesc, y); doc.text('NDIS LINE ITEM', cItem, y);
    doc.text('QTY', cQty, y); doc.text('RATE', cRate, y); doc.text('AMOUNT', R, y, { align: 'right' });
    y += 5; doc.line(48, y, R, y); y += 15;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    inv.line_items.forEach((li) => {
      const amt = li.amount ?? (li.qty || 0) * (li.rate || 0);
      doc.text(dmy(li.date), cD, y);
      doc.text(String(li.name || '').slice(0, 32), cDesc, y);
      doc.text(String(li.code || ''), cItem, y);
      doc.text(String(li.qty ?? 0), cQty, y);
      doc.text(money(li.rate || 0), cRate, y);
      doc.text(money(amt), R, y, { align: 'right' });
      y += 16;
    });
    // ── Total (NDIS is GST-free, so no GST line) ──
    y += 4; doc.setDrawColor(150); doc.line(cItem, y, R, y); y += 17;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text('INVOICE TOTAL', cItem, y); doc.text(money(inv.total), R, y, { align: 'right' });
    // ── Payment details (bottom-left) ──
    y += 36;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text('Payment details', 48, y); y += 15;
    doc.setFont('helvetica', 'normal');
    doc.text(`Account name: ${prov.bank_account_name || ''}`, 48, y); y += 14;
    doc.text(`BSB: ${prov.bank_bsb || ''}`, 48, y); y += 14;
    doc.text(`Account number: ${prov.bank_account_number || ''}`, 48, y);
    // ── Footer ──
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(150);
    doc.text('Generated by the hub ecosystems', 48, 800);
    return doc;
  };

  const buildPdfBase64 = (): string => {
    const uri = buildPdfDoc().output('datauristring');
    return uri.substring(uri.indexOf(',') + 1);
  };

  // Open the invoice as a real PDF in a new tab (mobile-friendly: blob URL,
  // with a download fallback if the browser blocks the popup).
  const previewPdf = (): void => {
    try {
      const url = URL.createObjectURL(buildPdfDoc().output('blob'));
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = `invoice-${inv.invoice_number || inv.id}.pdf`; a.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert('Could not generate the PDF preview.'); }
  };

  const REJECT_REASONS = [
    'Incorrect participant information',
    'Incorrect provider information',
    'Incorrect amount or dates',
    'Incorrect line items or codes',
  ];
  const openRejectModal = (): void => {
    const ov = document.createElement('div');
    ov.className = 'iv-modal-ov';
    ov.innerHTML = `<div class="iv-modal">
      <div class="iv-modal-h">Reject invoice</div>
      <p class="iv-modal-p">Why are you rejecting it? This goes back so a corrected invoice can be issued.</p>
      ${REJECT_REASONS.map((r, i) => `<label class="iv-rj-opt"><input type="radio" name="iv-rj" value="${r}"${i === 0 ? ' checked' : ''}> ${r}</label>`).join('')}
      <label class="iv-rj-opt"><input type="radio" name="iv-rj" value="__other"> Other</label>
      <textarea class="iv-rj-other" id="iv-rj-other" placeholder="Add details (required if 'Other')" rows="3"></textarea>
      <div class="iv-modal-btns">
        <button class="iv-btn iv-btn-edit" id="iv-rj-cancel">Cancel</button>
        <button class="iv-btn iv-btn-reject" id="iv-rj-send">Reject invoice</button>
      </div>
      <div id="iv-rj-res" style="color:#b4503c;font-size:13px;margin-top:8px"></div>
    </div>`;
    document.body.appendChild(ov);
    const close = (): void => ov.remove();
    ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
    ov.querySelector('#iv-rj-cancel')?.addEventListener('click', close);
    ov.querySelector('#iv-rj-send')?.addEventListener('click', async () => {
      const sel = ov.querySelector('input[name="iv-rj"]:checked') as HTMLInputElement | null;
      const other = (ov.querySelector('#iv-rj-other') as HTMLTextAreaElement).value.trim();
      let reason = sel?.value || '';
      if (reason === '__other') reason = other;
      else if (other) reason = `${reason} — ${other}`;
      const res = ov.querySelector('#iv-rj-res') as HTMLElement;
      if (!reason) { res.textContent = 'Pick a reason or type one.'; return; }
      const btn = ov.querySelector('#iv-rj-send') as HTMLButtonElement;
      btn.disabled = true; btn.textContent = 'Rejecting…';
      try {
        const r = await fetch(`${API_BASE}/api/ndis/approval/${encodeURIComponent(token)}/reject`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
        });
        const json = await r.json();
        if (json.status !== 'ok') throw new Error(json.error || 'reject failed');
        close();
        (page.querySelector('.iv-actions') as HTMLElement | null)?.remove();
        content.innerHTML = '<div class="iv-banner iv-ok">Invoice rejected. Thanks — the reason has been sent back for correction.</div>';
      } catch (e) { btn.disabled = false; btn.textContent = 'Reject invoice'; res.textContent = `Error: ${(e as Error).message}`; }
    });
  };

  const approve = async (): Promise<void> => {
    const confirm = document.getElementById('iv-confirm') as HTMLInputElement | null;
    if (confirm && !confirm.checked) { alert('Please tick "I confirm these details are correct" first.'); return; }
    const btn = document.getElementById('iv-approve') as HTMLButtonElement;
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    let pdf_base64 = '';
    try { pdf_base64 = buildPdfBase64(); } catch { /* PDF best-effort */ }
    try {
      const res = await fetch(`${API_BASE}/api/ndis/approval/${encodeURIComponent(token)}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_base64, filename: `invoice-${inv.invoice_number || inv.id}.pdf` }),
      });
      const json = await res.json();
      if (json.status !== 'ok') throw new Error(json.error);
      inv.status = 'sent'; editing = false; draw();
    } catch {
      if (btn) { btn.disabled = false; btn.textContent = 'Approve & send'; }
      alert('Could not approve. Please try again.');
    }
  };

  const submitEdits = async (): Promise<void> => {
    syncFromInputs();
    const btn = document.getElementById('iv-submit') as HTMLButtonElement;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }
    const provider_changes: Record<string, string> = {};
    const g = (id: string): string => (document.getElementById(id) as HTMLInputElement)?.value?.trim() || '';
    if (g('pv-name') && g('pv-name') !== (prov.display_name || '')) provider_changes.legal_name = g('pv-name');
    if (g('pv-abn') !== (prov.abn || '')) provider_changes.abn = g('pv-abn');
    if (g('pv-bsb') !== (prov.bank_bsb || '')) provider_changes.bank_bsb = g('pv-bsb');
    if (g('pv-acc') !== (prov.bank_account_number || '')) provider_changes.bank_account_number = g('pv-acc');
    const numEl = document.getElementById('iv-num') as HTMLInputElement | null;
    if (numEl) inv.invoice_number = numEl.value;
    inv.service_date_from = (document.getElementById('iv-from') as HTMLInputElement)?.value || inv.service_date_from;
    inv.service_date_to = (document.getElementById('iv-to') as HTMLInputElement)?.value || inv.service_date_to;
    try {
      const res = await fetch(`${API_BASE}/api/ndis/approval/${encodeURIComponent(token)}/edit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_items: inv.line_items, provider_changes: Object.keys(provider_changes).length ? provider_changes : undefined, note: 'Edited by provider on approval page' }),
      });
      const json = await res.json();
      if (json.status !== 'ok') throw new Error(json.error);
      inv.status = 'edited_pending_review'; editing = false; draw();
    } catch {
      if (btn) { btn.disabled = false; btn.textContent = 'Submit edits'; }
      alert('Could not submit changes. Please try again.');
    }
  };

  draw();
}
